import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';

import { AppliedLabelFilters } from './components/AppliedLabelFilters';
import { ArtistAppliedFilterListItem } from './components/ArtistAppliedFilterListItem';

// @TODO WMG1-678 - Move to shared location for roster and user management.
export interface ArtistAppliedLabelFilter {
  labelId: number;
  displayName: string;
  updatedAt?: string;
  updatedBy?: string;
}

interface ArtistAppliedFilterListProps {
  isCrossover: boolean;
  isOnRoster?: boolean;
  labelFilters: ArtistAppliedLabelFilter[];
  onRemoveCrossover: () => void;
  onRemoveOnRoster: () => void;
  onRemoveLabel: (labelId: number) => void;
  onClear: () => void;
  onMoreClick: () => void;
}

const MAX_VISIBLE_FILTERS = 3;

/**
 * This component is wrapped in React.memo() to prevent unnecessary re-renders with
 * the ScrollCollapse logic. See https://github.com/wmgtech/wmg2-one-app/pull/1736
 */
export const ArtistAppliedFilterList: React.FC<ArtistAppliedFilterListProps> =
  memo(
    ({
      isCrossover,
      isOnRoster,
      labelFilters,
      onRemoveCrossover,
      onRemoveOnRoster,
      onRemoveLabel,
      onClear,
      onMoreClick,
    }) => {
      const { t } = useTranslation('roster');
      const visibleLabelFilterCount =
        MAX_VISIBLE_FILTERS - (isCrossover ? 1 : 0);

      return (
        <div
          className="flex items-center gap-md py-md"
          aria-label="Applied filters"
        >
          <div className="text-body-sm">{t('Filter(s) applied:')}</div>
          {isCrossover ? (
            <ArtistAppliedFilterListItem
              label={t('crossover.label')}
              onRemove={onRemoveCrossover}
            />
          ) : null}
          {isOnRoster !== undefined ? (
            <ArtistAppliedFilterListItem
              label={t(
                isOnRoster
                  ? 'filters.onRoster.label'
                  : 'filters.offRoster.label',
              )}
              onRemove={onRemoveOnRoster}
            />
          ) : null}
          <AppliedLabelFilters
            onRemoveLabel={onRemoveLabel}
            onMoreClick={onMoreClick}
            labelFilters={labelFilters}
            visibleLabelFilterCount={visibleLabelFilterCount}
          />
          <Button
            variant="text"
            onClick={onClear}
            className="-ml-sm px-sm py-xs text-body-sm font-semibold"
          >
            {t('filters.clearFilters')}
          </Button>
        </div>
      );
    },
  );

ArtistAppliedFilterList.displayName = 'ArtistAppliedFilterList';
